// Vision zones (issue 1149; docs/VTT/design/30-sight-walls-and-light.md §1.3, §1.6, §3.1). A ZONE is a set of squares
// the DM paints, kept as row spans as the fog keeps its squares (`fog.ts`): grid indices on the fitted grid, so a
// refitted grid carries the zone with its squares. Each zone has a KIND, a closed vocabulary with a rule each, read by
// the sight code (`sight/sight.ts`):
//
// - `fog_bank`: its outline blocks sight, as a wall does.
// - `darkness` (magical darkness): its outline blocks sight in both directions; no light shines inside it and darkvision
//   does not see into it. Only blindsight perceives through it (§1.5).
// - `never_revealed`: its squares are never added to explored and are never seen; the DM's manual reveal shows them.
// - `difficult` (issue 1151): no effect on sight. Movement counts it: a step into one of its squares costs the system's
//   `distance.difficult.multiplier` (`routeCost`, by the ONE distance arithmetic in `grid/measure.ts`). A player is sent
//   its squares within their explored map only (`PlayerFog.difficult`), since their ruler and drag readout need them.
//
// - `sound` (issue 1162): no effect on sight or movement; an area the DM paints to tie a sound cue to (`cues.ts`).
//
// The vocabulary is a list (`ZONE_KINDS`) and a rule per kind (`ZONE_RULES`): #1151's difficult terrain is one more
// entry with no sight rule. Every zone event is the DM's alone, a player's is the bare `forbidden`, and a player is sent
// nothing of a zone as such (§4 names none) but difficult terrain's squares in their explored map: its effect is what they see.
import { coverSpans, FOG_LIMITS, footprintSquares, normaliseSpans, readSpans, revealSpans } from './fog.js';
import { pathCost } from '../grid/measure.js';
import { isGridless } from '../grid/grid.js';
export const ZONE_KINDS = ['fog_bank', 'darkness', 'never_revealed', 'difficult', 'sound'];
export const ZONE_RULES = {
    fog_bank: { outline: true, dark: false, unexplored: false },
    darkness: { outline: false, dark: true, unexplored: false },
    never_revealed: { outline: false, dark: false, unexplored: true },
    difficult: { outline: false, dark: false, unexplored: false },
    sound: { outline: false, dark: false, unexplored: false },
};
/** The words the DM's tool shows. */
export const ZONE_LABELS = { fog_bank: 'Fog bank', darkness: 'Magical darkness', never_revealed: 'Never revealed', difficult: 'Difficult terrain', sound: 'Sound area' };
/** `zones` a scene; `spans` a zone (normalised); `label`'s length. */
export const ZONE_LIMITS = { zones: 100, spans: 2000, label: 60 };
export const ZONES_FULL = 'This scene has as many zones as it can hold. Remove some first.';
export const ZONE_TOO_BIG = 'That zone cannot hold that many separate patches. Paint a larger area instead.';
export const ZONE_GONE = 'That zone is no longer on the map.';
export const ZONE_EVENT_KINDS = ['zone.paint', 'zone.erase', 'zone.set', 'zone.remove'];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isKind = (v) => ZONE_KINDS.includes(v);
const isIndex = (v) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= FOG_LIMITS.index;
/** One line of plain text: no control character at all (the review of 2026-10-10: a NUL was accepted). */
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isLabel = (v) => typeof v === 'string' && v.trim().length >= 1 && v.length <= ZONE_LIMITS.label && !LINE_BAD.test(v);
function parseRect(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { i0, j0, i1, j1 } = v;
    if (!isIndex(i0) || !isIndex(j0) || !isIndex(i1) || !isIndex(j1) || i0 > i1 || j0 > j1)
        return null;
    return { i0, j0, i1, j1 };
}
/** Strict: a uuid, a known kind, a bounded label, a box of whole indices in order. */
export function parseZoneBody(b) {
    switch (b.kind) {
        case 'zone.paint': {
            const rect = parseRect(b.rect);
            if (!isUuid(b.id) || !rect)
                return null;
            if (b.zone !== undefined && !isKind(b.zone))
                return null;
            if (b.label !== undefined && !isLabel(b.label))
                return null;
            return { kind: 'zone.paint', id: b.id, rect, ...(b.zone !== undefined ? { zone: b.zone } : {}), ...(b.label !== undefined ? { label: b.label } : {}) };
        }
        case 'zone.erase': {
            const rect = parseRect(b.rect);
            if (!rect || (b.id !== undefined && !isUuid(b.id)))
                return null;
            return { kind: 'zone.erase', rect, ...(b.id !== undefined ? { id: b.id } : {}) };
        }
        case 'zone.set': {
            if (!isUuid(b.id))
                return null;
            const out = { kind: 'zone.set', id: b.id };
            if (b.zone !== undefined) {
                if (!isKind(b.zone))
                    return null;
                out.zone = b.zone;
            }
            if (b.label !== undefined) {
                if (b.label !== null && !isLabel(b.label))
                    return null;
                out.label = b.label;
            }
            return out.zone === undefined && out.label === undefined ? null : out;
        }
        case 'zone.remove':
            if (!Array.isArray(b.ids) || b.ids.length < 1 || b.ids.length > ZONE_LIMITS.zones || !b.ids.every(isUuid))
                return null;
            return { kind: 'zone.remove', ids: [...new Set(b.ids)] };
        default: return null;
    }
}
/** A zone read from a snapshot, or null when unreadable. */
function readZone(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const z = v;
    if (!isUuid(z.id) || !isKind(z.kind))
        return null;
    const spans = readSpans(z.spans).slice(0, ZONE_LIMITS.spans);
    if (!spans.length)
        return null;
    return { id: z.id, kind: z.kind, ...(isLabel(z.label) ? { label: z.label } : {}), spans };
}
export function normaliseZones(v) {
    if (!Array.isArray(v))
        return [];
    const out = [];
    for (const z of v.map(readZone))
        if (z && !out.some((x) => x.id === z.id) && out.length < ZONE_LIMITS.zones)
            out.push(z);
    return out;
}
export const copyZones = (zones) => zones.map((z) => ({ ...z, spans: z.spans.map((s) => ({ ...s })) }));
const boxSpans = (r) => revealSpans([], r);
export function reduceZones(zones, body) {
    switch (body.kind) {
        case 'zone.paint': {
            const had = zones.find((z) => z.id === body.id);
            if (!had) {
                if (!body.zone)
                    return zones;
                return [...zones, { id: body.id, kind: body.zone, ...(body.label ? { label: body.label } : {}), spans: boxSpans(body.rect) }];
            }
            return zones.map((z) => (z.id === body.id ? { ...z, ...(body.zone ? { kind: body.zone } : {}), ...(body.label ? { label: body.label } : {}), spans: normaliseSpans([...z.spans, ...boxSpans(body.rect)]) } : z));
        }
        case 'zone.erase': {
            let changed = false;
            const out = [];
            for (const z of zones) {
                if (body.id !== undefined && z.id !== body.id) {
                    out.push(z);
                    continue;
                }
                const spans = coverSpans(z.spans, body.rect);
                if (spans.length === z.spans.length && spans.every((s, k) => s.i0 === z.spans[k].i0 && s.i1 === z.spans[k].i1 && s.j === z.spans[k].j)) {
                    out.push(z);
                    continue;
                }
                changed = true;
                if (spans.length)
                    out.push({ ...z, spans });
            }
            return changed ? out : zones;
        }
        case 'zone.set':
            return zones.map((z) => {
                if (z.id !== body.id)
                    return z;
                const next = { ...z, ...(body.zone ? { kind: body.zone } : {}) };
                if (body.label === null)
                    delete next.label;
                else if (body.label !== undefined)
                    next.label = body.label;
                return next;
            });
        case 'zone.remove': {
            const gone = new Set(body.ids);
            return zones.some((z) => gone.has(z.id)) ? zones.filter((z) => !gone.has(z.id)) : zones;
        }
        default: return zones;
    }
}
/** Why the DM's zone event is refused (null: it stands). The role is the caller's (`forbidden` for anyone else). */
export function zoneRefusal(zones, body) {
    switch (body.kind) {
        case 'zone.paint': {
            const had = zones.find((z) => z.id === body.id);
            if (!had) {
                if (!body.zone)
                    return 'Choose what kind of zone to paint.';
                if (zones.length >= ZONE_LIMITS.zones)
                    return ZONES_FULL;
            }
            const spans = reduceZones([...zones], body).find((z) => z.id === body.id)?.spans ?? [];
            return spans.length > ZONE_LIMITS.spans ? ZONE_TOO_BIG : null;
        }
        case 'zone.erase': {
            if (body.id !== undefined && !zones.some((z) => z.id === body.id))
                return ZONE_GONE;
            // Erasing splits a span into two at most a row: refuse a cut that would leave a zone past its bound.
            return reduceZones([...zones], body).some((z) => z.spans.length > ZONE_LIMITS.spans) ? ZONE_TOO_BIG : null;
        }
        case 'zone.set': return zones.some((z) => z.id === body.id) ? null : ZONE_GONE;
        case 'zone.remove': return body.ids.some((id) => zones.some((z) => z.id === id)) ? null : 'Those zones are no longer on the map.';
        default: return null;
    }
}
/**
 * A set of squares' outline as wall segments along the grid's lines (§1.3: "a zone's outline is turned into wall
 * segments along its squares' edges"): each edge of a square whose neighbour across it is not in the set, joined end to
 * end along a line, so a block is four segments and an L six. `xs`/`ys` are the grid's line positions (`axisPositions`),
 * so a fitted, uneven grid outlines its own squares. Squares off the board are ignored.
 */
export function outlineOf(squares, cols, rows, xs, ys) {
    const inSet = (i, j) => i >= 0 && j >= 0 && i < cols && j < rows && squares.has(j * cols + i);
    const out = [];
    const edge = (x0, y0, x1, y1) => ({ a: { x: x0, y: y0 }, b: { x: x1, y: y1 }, move: false, sight: true });
    // Horizontal edges: on line j (above row j), runs of i where exactly one of rows j-1 and j is in the set.
    for (let j = 0; j <= rows; j += 1) {
        let start = -1;
        for (let i = 0; i <= cols; i += 1) {
            const on = i < cols && inSet(i, j - 1) !== inSet(i, j);
            // A run also breaks where the side the set lies on flips, so each segment bounds the set on one side only.
            const side = on ? inSet(i, j) : null;
            const prevSide = start >= 0 ? inSet(i - 1, j) : null;
            if (start >= 0 && (!on || side !== prevSide)) {
                out.push(edge(xs[start], ys[j], xs[i], ys[j]));
                start = -1;
            }
            if (on && start < 0)
                start = i;
        }
    }
    // Vertical edges: on line i (left of column i), runs of j.
    for (let i = 0; i <= cols; i += 1) {
        let start = -1;
        for (let j = 0; j <= rows; j += 1) {
            const on = j < rows && inSet(i - 1, j) !== inSet(i, j);
            const side = on ? inSet(i, j) : null;
            const prevSide = start >= 0 ? inSet(i, j - 1) : null;
            if (start >= 0 && (!on || side !== prevSide)) {
                out.push(edge(xs[i], ys[start], xs[i], ys[j]));
                start = -1;
            }
            if (on && start < 0)
                start = j;
        }
    }
    return out;
}
// ------------------------------------------------------------------------------------------------ difficult terrain (#1151)
/** Every square of difficult terrain on the scene, as spans. */
export const difficultSpans = (zones) => normaliseSpans(zones.filter((z) => z.kind === 'difficult').flatMap((z) => z.spans));
/** Whether a square is in a set of spans: rows looked up once. */
export function spanTest(spans) {
    const rows = new Map();
    for (const s of spans) {
        const r = rows.get(s.j);
        if (r)
            r.push(s);
        else
            rows.set(s.j, [s]);
    }
    return (c) => (rows.get(c.j) ?? []).some((s) => c.i >= s.i0 && c.i <= s.i1);
}
/**
 * What a move along `route` (board points, as `object.move` carries them) costs a thing of `size` standing at `start`,
 * by the system's distance rule and the scene's difficult squares (issue 1151). Each point is the square of its
 * footprint's top-left; a step is difficult when ANY square of the footprint it enters is (a large creature pays for
 * terrain under any part of it). Null on a gridless map or with no distance rule: nothing is counted.
 */
export function routeCost(start, route, size, grid, extent, distance, difficult) {
    if (!distance || isGridless(grid))
        return null;
    const first = footprintSquares(grid, extent, start, size);
    const w = first.i1 - first.i0;
    const h = first.j1 - first.j0;
    const cells = [start, ...route].map((p) => { const b = footprintSquares(grid, extent, p, size); return { i: b.i0, j: b.j0 }; });
    const inSpans = spanTest(difficult);
    const isDifficult = (c) => {
        for (let j = c.j; j <= c.j + h; j += 1)
            for (let i = c.i; i <= c.i + w; i += 1)
                if (inSpans({ i, j }))
                    return true;
        return false;
    };
    return pathCost(cells, distance.diagonal, isDifficult, distance.difficult?.multiplier ?? null);
}
//# sourceMappingURL=zones.js.map